import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PriceSummary } from "../components/PriceSummary";
import api, { AED } from "../lib/api";
import { CheckCircle2, MapPin, CalendarDays, Truck, Store, Loader2, Fuel, LifeBuoy, ShieldAlert, CreditCard } from "lucide-react";
import dayjs from "dayjs";

export default function Confirmation() {
  const { t } = useTranslation();
  const { ref } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${ref}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  }, [ref]);

  if (error)
    return (
      <div className="min-h-screen flex flex-col bg-[#fafcfa]">
        <Header />
        <div className="flex-1 flex items-center justify-center text-sm text-[#57685e]">{t("Booking not found.")} <Link to="/account" className="underline ml-1">{t("Go to My Bookings")}</Link></div>
        <Footer />
      </div>
    );

  if (!booking)
    return (
      <div className="min-h-screen flex flex-col bg-[#fafcfa]">
        <Header />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>
        <Footer />
      </div>
    );

  const delivery = booking.delivery?.enabled;

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">
        <div className="text-center mb-8 fade-up" data-testid="confirmation-header">
          <CheckCircle2 className="w-16 h-16 text-[#009900] mx-auto mb-4" />
          <h1 className="heading-upper text-2xl sm:text-3xl text-[#144733]">{t("You're all set. The road awaits.")}</h1>
          <p className="text-sm text-[#57685e] mt-2">{t("A confirmation email has been sent to {{email}} (demo mode)", { email: booking.driver.email })}</p>
          <div className="inline-block bg-[#144733] text-white rounded-lg px-6 py-3 mt-4">
            <div className="text-[10px] uppercase tracking-widest text-[#FFDC00]">{t("Booking reference")}</div>
            <div className="text-2xl font-bold tracking-wider" data-testid="booking-reference">{booking.booking_ref}</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 mb-6" data-testid="confirmation-details">
          <div className="flex items-center gap-4 pb-4 border-b border-[#e2ebe4]">
            <img src={booking.vehicle.image} alt={booking.vehicle.name} className="w-28 h-20 object-cover rounded" />
            <div>
              <span className="text-[10px] uppercase tracking-widest text-[#57685e]">{booking.vehicle.category}</span>
              <h2 className="text-lg font-bold text-[#0F1E17]">{booking.vehicle.name} <span className="text-xs font-normal text-[#57685e]">{t("or similar")}</span></h2>
              <span className="text-xs bg-[#f0f9f0] text-[#009900] font-semibold px-2 py-0.5 rounded uppercase tracking-wide">
                {booking.mode === "monthly" ? t("Monthly · {{n}} mo · {{km}} km/mo", { n: booking.months, km: booking.mileage_package?.toLocaleString() }) : t("Short-term rental")}
              </span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-4 text-sm">
            <div className="flex items-start gap-2">
              <CalendarDays className="w-4 h-4 text-[#009900] mt-0.5" />
              <div>
                <div className="font-semibold text-[#0F1E17]">{dayjs(booking.pickup_datetime).format("ddd, DD MMM YYYY · HH:mm")}</div>
                <div className="text-xs text-[#57685e]">{t("until")} {dayjs(booking.return_datetime).format("ddd, DD MMM YYYY · HH:mm")}</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              {delivery ? <Truck className="w-4 h-4 text-[#009900] mt-0.5" /> : <Store className="w-4 h-4 text-[#009900] mt-0.5" />}
              <div>
                <div className="font-semibold text-[#0F1E17]">{delivery ? t("Doorstep delivery") : t("Branch pickup")}</div>
                <div className="text-xs text-[#57685e]">
                  {delivery
                    ? t("{{address}} · {{time}} slot", { address: booking.delivery.address, time: `${String(Math.floor(booking.delivery.hour)).padStart(2, "0")}:${booking.delivery.hour % 1 ? "30" : "00"}` })
                    : booking.pickup_station}
                  {booking.flight?.number && t(" · Flight {{n}} tracked", { n: booking.flight.number })}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 sm:col-span-2">
              <MapPin className="w-4 h-4 text-[#009900] mt-0.5" />
              <div className="text-xs text-[#57685e]">
                {t("Payment:")} <span className="font-semibold text-[#0F1E17]">{booking.payment.status === "paid" ? t("Paid (demo): {{amount}}", { amount: AED(booking.quote.total) }) : t("Pay at counter: {{amount}}", { amount: AED(booking.quote.total) })}</span>
                {booking.payment.transaction_id && <span>{t(" · Ref {{id}}", { id: booking.payment.transaction_id })}</span>}
              </div>
            </div>
          </div>
        </div>

        <PriceSummary quote={booking.quote} testIdPrefix="confirmation-summary" />

        {/* Post-confirmation document capture invitation */}
        <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 mt-6" data-testid="doc-upload-invite">
          <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-2">{t("Complete your file in 2 minutes")}</h3>
          <p className="text-xs text-[#57685e] mb-3">
            {t("Upload your Emirates ID or passport plus your driving license so we can verify the driver before handover. It keeps your pickup or delivery quick and paper-free. Camera or file upload, JPEG/PNG/PDF up to 5 MB.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/account?tab=documents" className="btn-cta rounded px-5 py-2.5 text-xs text-center" data-testid="upload-docs-now-btn">{t("Upload documents now")}</Link>
            <Link to={`/checkin/${booking.booking_ref}`} className="border-2 border-[#009900] text-[#009900] rounded px-5 py-2.5 text-xs font-medium uppercase tracking-wide text-center hover:bg-[#f0f9f0]" data-testid="checkin-now-btn">{t("Check in online")}</Link>
          </div>
        </div>

        {/* Good to know before you drive */}
        <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-lg p-5 mt-6" data-testid="confirmation-reminders">
          <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3">{t("A few good-to-knows before you drive")}</h3>
          <ul className="text-xs text-[#144733] space-y-2.5">
            <li className="flex gap-2.5"><Fuel className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t("Return the car at the same fuel level it was handed over, so there's nothing extra to settle.")}</span></li>
            <li className="flex gap-2.5"><LifeBuoy className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t("24/7 roadside assistance is included, so save 800 EUROPCAR in your phone before you set off.")}</span></li>
            <li className="flex gap-2.5"><ShieldAlert className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t("In any accident, make sure everyone is safe, obtain the police report, then call 800 EUROPCAR. A reference number alone isn't the final report.")}</span></li>
            <li className="flex gap-2.5"><CreditCard className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" /><span>{t("Tolls (Salik in Dubai, Darb in Abu Dhabi) and any fines are billed after your rental, itemised in your account with the admin fee shown up front.")}</span></li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${booking.booking_ref}/invoice.pdf`} target="_blank" rel="noreferrer"
            className="border-2 border-[#144733] text-[#144733] font-medium uppercase text-sm tracking-wide rounded px-6 py-3 text-center flex-1 hover:bg-[#f0f5f0] transition-colors"
            data-testid="confirmation-invoice-btn">{t("Download invoice (PDF)")}</a>
          <Link to="/account" className="btn-cta rounded px-6 py-3 text-sm text-center flex-1" data-testid="goto-account-btn">{t("Manage in My Bookings")}</Link>
          <Link to="/" className="border-2 border-[#009900] text-[#009900] font-medium uppercase text-sm tracking-wide rounded px-6 py-3 text-center flex-1 hover:bg-[#f0f9f0] transition-colors" data-testid="book-another-btn">{t("Book another car")}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
